/**
 * CART COMPONENT BASE UTILITIES
 *
 * Comprehensive base class for all cart-related Web Components, providing standardized patterns
 * for cart state management, event handling, error management, and component lifecycle.
 * Integrates seamlessly with CartStateManager for real-time cart operations.
 *
 * Used by: CartDrawer, CartItem, CartItems, CartGWPSelector, CartNote, CartDiscounts,
 *          CartAnnouncement, FreeShippingBanner, BuyMoreSaveMoreBanner
 *
 * Key Features:
 * - Unified cart state manager access via `this.cartStateManager` getter
 * - Standardized initialization sequence with async support
 * - Advanced event management with capture/replay for early events
 * - Comprehensive error handling with user feedback
 * - Loading state management with visual feedback
 * - Memory-efficient cleanup and resource management
 * - Debug logging support with component-specific namespacing
 * - DOM element caching for performance
 * - Timeout and AbortController tracking for proper cleanup
 *
 * Component Lifecycle:
 * 1. Constructor - Set up initial state and bind methods
 * 2. connectedCallback - Trigger initialization when added to DOM
 * 3. _initialize() - Execute standardized initialization sequence
 * 4. _cacheElements() - Cache frequently accessed DOM elements
 * 5. _initializeComponent() - Component-specific initialization (override)
 * 6. _setupEventListeners() - Set up event listeners (override)
 * 7. disconnectedCallback - Clean up when removed from DOM
 *
 * Event System Integration:
 * - Pub-sub subscriptions via _subscribe() with automatic cleanup
 * - Sticky events in the pub-sub bus handle late-initialization replay
 * - DOM event listeners via _addEventListener() with automatic cleanup
 * - All subscriptions cleaned up automatically on disconnectedCallback
 *
 * Cart State Integration:
 * - Direct access to CartStateManager via `this.cartStateManager`
 * - Automatic waiting for cart state manager availability
 * - Integration with cart state for dynamic UI updates
 * - Real-time state synchronization across all components
 *
 * Error Handling:
 * - Centralized error handling with consistent logging
 * - User-friendly error messages with auto-hide functionality
 * - Error event dispatching for system-wide error management
 * - Recovery patterns for common failure scenarios
 *
 * @example
 * // Basic component extension
 * class MyCartComponent extends CartComponentBase {
 *   async _initializeComponent() {
 *     await this.waitForCartStateManager();
 *     const state = this.cartStateManager.getState();
 *     console.log('Current cart:', state);
 *   }
 *
 *   _setupEventListeners() {
 *     this._subscribe(EVENTS.CART_STATE_CHANGED, this.handleCartChange.bind(this));
 *   }
 * }
 *
 * @example
 * // Debug mode usage
 * <my-cart-component class="debug"></my-cart-component>
 *
 * @namespace CartComponentBaseUtilities
 * @since 2024
 * @version 2.0.0
 */

import { getCartStateManager } from '@utilities/cart-state-manager';
import { subscribe as pubsubSubscribe, EVENTS } from '@utilities/events';
import { parseConfig } from '@utilities/theme';

/**
 * Cart Component Base Class
 *
 * Provides standardized patterns for all cart-related components including:
 * - Debug logging
 * - Error handling
 * - Loading state management
 * - DOM element caching
 * - Event listener management
 * - Initialization sequence
 * - Memory cleanup
 * - Centralized cart state manager access via `this.cartStateManager`
 *
 * ## Usage
 * Subclasses should access the cart state manager using:
 * ```javascript
 * const stateManager = this.cartStateManager;
 * ```
 *
 * Do NOT create redundant `getCartStateManager()` methods in subclasses.
 *
 * @class CartComponentBase
 * @extends HTMLElement
 */
export class CartComponentBase extends HTMLElement {
  constructor() {
    super();

    /** @type {Object} Cached DOM elements */
    this.elements = {};

    /** @type {Object} Template elements */
    this.templates = {};

    /** @type {Object} Component configuration */
    this.config = null;

    /** @type {HTMLElement|null} Parent cart drawer */
    this.cartDrawer = null;

    /** @type {boolean} Whether component is currently loading */
    this.isLoading = false;

    /** @type {Array<number>} Active timeout IDs for cleanup */
    this.activeTimeouts = [];

    /** @type {Array<AbortController>} Active abort controllers for cleanup */
    this.activeControllers = [];

    /** @type {Map<string, Array>} Event listeners for cleanup */
    this.eventListeners = new Map();

    /** @type {Array<Function>} Pub-sub unsubscribe functions for cleanup */
    this.pubsubUnsubscribers = [];
  }

  /**
   * Checks if debug mode is enabled for this component
   * @returns {boolean} Whether debug logging is enabled
   */
  allowDebug() {
    return this.classList.contains("debug");
  }

  /**
   * Standard error handling with consistent logging and user feedback
   * @param {Error} error - The error that occurred
   * @param {string} context - Context where the error occurred
   * @param {boolean} showUser - Whether to show error to user
   * @protected
   */
  _handleError(error, context = '', showUser = false) {
    if (this.allowDebug()) {
      console.error(`${this.constructor.name}: ${context}`, error);
    }

    if (showUser) {
      this._showUserMessage(error.message || 'An error occurred', 'error');
    }
  }

  /**
   * Set loading state with visual feedback
   * @param {boolean} isLoading - Whether component is loading
   * @param {string} context - Context for the loading state
   * @protected
   */
  _setLoadingState(isLoading, context = '') {
    this.isLoading = isLoading;
    this.classList.toggle(`${this.tagName.toLowerCase()}--loading`, isLoading);

    if (isLoading) {
      this._showSpinner();
    } else {
      this._hideSpinner();
    }

    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: Loading state ${isLoading ? 'start' : 'end'} - ${context}`);
    }
  }

  /**
   * Show loading spinner
   * @protected
   */
  _showSpinner() {
    const spinner = this.elements.spinner || this.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.style.display = 'block';
    }
  }

  /**
   * Hide loading spinner
   * @protected
   */
  _hideSpinner() {
    const spinner = this.elements.spinner || this.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.style.display = 'none';
    }
  }

  /**
   * Get the cart state manager instance
   *
   * This is the standardized way for all CartComponentBase subclasses to access
   * the global cart state manager. Subclasses should use `this.cartStateManager`
   * rather than importing and calling `getCartStateManager()` directly.
   *
   * @returns {CartStateManager|null} The cart state manager instance
   * @protected
   */
  get cartStateManager() {
    return getCartStateManager();
  }

  /**
   * Get current cart state from the state manager
   * @returns {Object|null} Current cart state
   * @protected
   */
  getCartState() {
    return this.cartStateManager?.getState() || null;
  }

  /**
   * Wait for the cart state manager to be available and initialized
   *
   * Critical helper method that ensures components don't attempt to access cart state
   * before the CartStateManager is fully initialized. This prevents race conditions
   * during component startup and ensures reliable cart operations.
   *
   * The method uses multiple strategies:
   * - Immediate return if manager is already ready
   * - Event listeners for greyson:cart:system:ready and greyson:cart:initial-load
   * - Polling fallback every 100ms
   * - Configurable timeout with proper cleanup
   *
   * Use this in _initializeComponent() when you need immediate access to cart state
   * during component initialization, or before any cart operations that require
   * the state manager to be fully ready.
   *
   * @param {number} [timeout=10000] - Maximum wait time in milliseconds
   * @returns {Promise<CartStateManager>} Promise resolving with initialized cart state manager
   * @throws {Error} If cart state manager is not ready within timeout period
   * @protected
   *
   * @example
   * // In component initialization
   * async _initializeComponent() {
   *   const stateManager = await this.waitForCartStateManager();
   *   const currentState = stateManager.getState();
   *   this.updateUI(currentState);
   * }
   *
   * @example
   * // With custom timeout
   * const stateManager = await this.waitForCartStateManager(5000);
   */
  async waitForCartStateManager(timeout = 10000) {
    const startTime = Date.now();

    // If cart state manager is already available and initialized, return it immediately
    const existingManager = this.cartStateManager;
    if (existingManager && existingManager.initialStateLoaded) {
      if (this.allowDebug()) {
        console.log(`${this.constructor.name}: Cart state manager already ready`);
      }
      return existingManager;
    }

    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: Waiting for cart state manager to be ready...`, {
        hasManager: !!existingManager,
        initialStateLoaded: existingManager?.initialStateLoaded
      });
    }

    return new Promise((resolve, reject) => {
      let settled = false;

      const timeoutId = setTimeout(() => {
        if (!settled) {
          settled = true;
          unsubSystem();
          unsubLoad();
          clearInterval(pollInterval);
          if (this.allowDebug()) {
            console.warn(`${this.constructor.name}: Cart state manager wait timeout after ${timeout}ms`);
          }
          reject(new Error(`Cart state manager not ready within ${timeout}ms`));
        }
      }, timeout);
      this.activeTimeouts.push(timeoutId);

      const checkAndResolve = () => {
        const manager = this.cartStateManager;
        if (manager && manager.initialStateLoaded) {
          if (settled) return true;
          settled = true;
          clearTimeout(timeoutId);
          unsubSystem();
          unsubLoad();
          clearInterval(pollInterval);
          if (this.allowDebug()) {
            console.log(`${this.constructor.name}: Cart state manager ready after ${Date.now() - startTime}ms`);
          }
          resolve(manager);
          return true;
        }
        return false;
      };

      if (checkAndResolve()) return;

      const unsubSystem = pubsubSubscribe(EVENTS.CART_SYSTEM_READY, () => checkAndResolve());
      const unsubLoad = pubsubSubscribe(EVENTS.CART_STATE_INITIAL_LOAD, () => checkAndResolve());

      const pollInterval = setInterval(() => checkAndResolve(), 100);
    });
  }

  /**
   * Cache frequently accessed DOM elements
   * Override in subclasses to define component-specific elements
   * @protected
   */
  _cacheElements() {
    this.elements = {
      spinner: this.querySelector('.loading-overlay__spinner'),
      errorContainer: this.querySelector('.error-container'),
      messageContainer: this.querySelector('.message-container')
    };

    this.templates = {};

    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: Elements cached`, this.elements);
    }
  }

  /**
   * Standard initialization sequence
   * Override _initializeComponent() in subclasses for component-specific logic
   * @protected
   */
  async _initialize() {
    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: Starting initialization`);
    }

    try {
      // 1. Cache DOM elements first
      this._cacheElements();

      // 2. Parse configuration using DOMUtils
      try {
        this.config = parseConfig(this, this.allowDebug());
      } catch (error) {
        console.error(`${this.constructor.name}: Error parsing config:`, error);
        this.config = {};
      }

      // 3. Dependencies are now imported via ES6 modules - no need to wait

      // 4. Component-specific initialization
      await this._initializeComponent();

      // 5. Set up event listeners
      this._setupEventListeners();

      if (this.allowDebug()) {
        console.log(`${this.constructor.name}: Initialization complete`);
      }
    } catch (error) {
      this._handleError(error, 'initialization', true);
    }
  }

  /**
   * Component-specific initialization logic
   * Override in subclasses
   * @protected
   */
  async _initializeComponent() {
    // Override in subclasses
  }

  /**
   * Set up event listeners
   * Override in subclasses
   * @protected
   */
  _setupEventListeners() {
    // Override in subclasses
  }



  /**
   * Show user message with auto-hide
   * @param {string} message - Message to display
   * @param {string} type - Message type (info, error, success, warning)
   * @param {number} duration - Auto-hide duration in ms (0 = no auto-hide)
   * @protected
   */
  _showUserMessage(message, type = 'info', duration = 5000) {
    const container = this.elements.messageContainer;
    if (!container) return;

    container.textContent = message;
    container.className = `user-message user-message--${type}`;
    container.classList.remove('hidden');

    if (duration > 0) {
      const timeoutId = setTimeout(() => {
        this._hideUserMessage();
      }, duration);
      this.activeTimeouts.push(timeoutId);
    }
  }

  /**
   * Hide user message
   * @protected
   */
  _hideUserMessage() {
    const container = this.elements.messageContainer;
    if (container) {
      container.classList.add('hidden');
    }
  }

  /**
   * Add DOM event listener with automatic cleanup tracking.
   * For pub-sub events, use _subscribe() instead.
   * @param {EventTarget} target - Event target
   * @param {string} type - Event type
   * @param {Function} listener - Event listener
   * @param {Object} options - Event listener options
   * @protected
   */
  _addEventListener(target, type, listener, options = {}) {
    target.addEventListener(type, listener, options);

    const key = `${target.constructor.name}-${type}`;
    if (!this.eventListeners.has(key)) {
      this.eventListeners.set(key, []);
    }
    this.eventListeners.get(key).push({ target, type, listener, options });

    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: Added event listener for ${type} on ${target.constructor.name}`);
    }
  }

  /**
   * Remove a specific event listener
   * @param {EventTarget} target - Event target
   * @param {string} type - Event type
   * @param {Function} listener - Event listener to remove
   * @protected
   */
  _removeEventListener(target, type, listener) {
    target.removeEventListener(type, listener);

    // Remove from tracking
    const key = `${target.constructor.name}-${type}`;
    if (this.eventListeners.has(key)) {
      const listeners = this.eventListeners.get(key);
      const index = listeners.findIndex(l => l.listener === listener);
      if (index > -1) {
        listeners.splice(index, 1);
        if (listeners.length === 0) {
          this.eventListeners.delete(key);
        }
      }
    }

    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: Removed event listener for ${type} on ${target.constructor.name}`);
    }
  }

  /**
   * Subscribe to a pub-sub event with automatic cleanup on disconnect.
   *
   * Sticky events (greyson:cart:changed, greyson:cart:system:ready,
   * greyson:cart:initial-load) replay the latest value immediately,
   * eliminating race conditions without a separate capture/replay mechanism.
   *
   * @param {string} eventName - Pub-sub event name (e.g. 'greyson:cart:changed')
   * @param {Function} callback - Invoked with the published data object directly.
   * @returns {Function} Unsubscribe function
   * @protected
   */
  _subscribe(eventName, callback) {
    const unsubscribe = pubsubSubscribe(eventName, callback);
    this.pubsubUnsubscribers.push(unsubscribe);

    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: Subscribed to ${eventName} via pub-sub`);
    }

    return unsubscribe;
  }

  /**
   * Create a timeout with automatic cleanup tracking
   * @param {Function} callback - Callback function
   * @param {number} delay - Delay in milliseconds
   * @returns {number} Timeout ID
   * @protected
   */
  _setTimeout(callback, delay) {
    const timeoutId = setTimeout(callback, delay);
    this.activeTimeouts.push(timeoutId);
    return timeoutId;
  }

  /**
   * Create an AbortController with automatic cleanup tracking
   * @returns {AbortController} Abort controller
   * @protected
   */
  _createAbortController() {
    const controller = new AbortController();
    this.activeControllers.push(controller);
    return controller;
  }

  /**
   * Clear all timeouts
   * @protected
   */
  _clearTimeouts() {
    this.activeTimeouts.forEach(timeoutId => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    });
    this.activeTimeouts = [];
  }

  /**
   * Remove all tracked event listeners and pub-sub subscriptions
   * @protected
   */
  _removeEventListeners() {
    this.eventListeners.forEach((listeners) => {
      listeners.forEach(({ target, type, listener, options }) => {
        target.removeEventListener(type, listener, options);
      });
    });
    this.eventListeners.clear();

    this.pubsubUnsubscribers.forEach((unsub) => unsub());
    this.pubsubUnsubscribers = [];
  }

  /**
   * Abort all tracked controllers
   * @protected
   */
  _abortControllers() {
    this.activeControllers.forEach(controller => {
      if (!controller.signal.aborted) {
        controller.abort();
      }
    });
    this.activeControllers = [];
  }

  /**
   * Clear object references
   * @protected
   */
  _clearReferences() {
    this.cartDrawer = null;
    this.elements = {};
    this.templates = {};
    this.config = null;
  }

  /**
   * Standard cleanup sequence
   * @protected
   */
  _cleanup() {
    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: Cleaning up component`);
    }

    // Clear all timeouts/intervals
    this._clearTimeouts();

    // Remove all event listeners
    this._removeEventListeners();

    // Abort all controllers
    this._abortControllers();

    // Clear references
    this._clearReferences();
  }

  /**
   * Web Components lifecycle - called when connected to DOM
   */
  async connectedCallback() {
    await this._initialize();
  }

  /**
   * Web Components lifecycle - called when disconnected from DOM
   */
  disconnectedCallback() {
    this._cleanup();
  }
}

export default CartComponentBase;
